/**
 * Encryption for private viewing data.
 *
 * Envelope encryption, which is more machinery than encrypting straight with a
 * passphrase-derived key but buys something specific:
 *
 *     passphrase --PBKDF2--> KEK --unwraps--> DEK --encrypts--> the data
 *
 * The data encryption key is random and never changes. The passphrase only
 * ever protects *it*. Changing the passphrase therefore rewraps a single 32
 * byte key rather than re-encrypting every record in the vault, which for a
 * library of any size is the difference between instant and unusable.
 *
 * AES-GCM throughout, so every decryption is authenticated. A record that has
 * been altered on disk fails to decrypt rather than returning damaged data,
 * and that failure is what the tamper detection test asserts.
 */

/**
 * PBKDF2 iterations.
 *
 * OWASP's floor for PBKDF2-SHA256 is 600,000. This is the one deliberately
 * slow operation in the application: it runs once at unlock, and its cost is
 * the entire defence against an offline attack on a weak passphrase.
 */
export const KDF_ITERATIONS = 600_000;

const SALT_BYTES = 16;
const IV_BYTES = 12; // 96 bits, the size AES-GCM is specified for.
const DEK_BYTES = 32;

/** A vault's stored metadata. Safe to keep in the clear; holds no secret. */
export interface VaultRecord {
  readonly version: 1;
  /** Base64 salt for the passphrase derivation. */
  readonly salt: string;
  readonly iterations: number;
  /** The data key, encrypted under the passphrase-derived key. */
  readonly wrappedKey: SealedData;
}

/** Ciphertext plus the nonce it was produced with. */
export interface SealedData {
  /** Base64 initialisation vector. Unique per encryption, never reused. */
  readonly iv: string;
  /** Base64 ciphertext with the GCM authentication tag appended. */
  readonly data: string;
}

/**
 * An unlocked data key.
 *
 * Deliberately opaque. The underlying `CryptoKey` is created as
 * non-extractable, so even code holding this object cannot read the key bytes
 * back out of it.
 */
export interface VaultKey {
  readonly __brand: 'VaultKey';
  readonly key: CryptoKey;
}

const subtle = (): SubtleCrypto => {
  const c = globalThis.crypto;
  if (c?.subtle === undefined) {
    throw new Error('WebCrypto is unavailable, so private viewing cannot be secured');
  }
  return c.subtle;
};

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Stretch a passphrase into a key encryption key. */
async function deriveKek(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const material = await subtle().importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return subtle().deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    // Not extractable: this key exists only to wrap and unwrap the data key.
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

/**
 * Create a new vault.
 *
 * Generates a fresh random data key and seals it under the passphrase. The
 * returned record contains no secret and can be stored in the clear.
 */
export async function createVault(
  passphrase: string,
  /**
   * Overridable so the test suite can run in milliseconds rather than minutes.
   * Never lower it in application code: this cost *is* the protection against
   * an offline attack on a weak passphrase.
   */
  iterations: number = KDF_ITERATIONS,
): Promise<{ readonly record: VaultRecord; readonly key: VaultKey }> {
  if (passphrase.length === 0) throw new Error('A vault passphrase cannot be empty');

  const salt = randomBytes(SALT_BYTES);
  const kek = await deriveKek(passphrase, salt, iterations);

  const dek = await subtle().generateKey({ name: 'AES-GCM', length: DEK_BYTES * 8 }, true, [
    'encrypt',
    'decrypt',
  ]);

  const iv = randomBytes(IV_BYTES);
  const wrapped = await subtle().wrapKey('raw', dek, kek, {
    name: 'AES-GCM',
    iv: iv as BufferSource,
  });

  return {
    record: {
      version: 1,
      salt: toBase64(salt),
      iterations,
      wrappedKey: { iv: toBase64(iv), data: toBase64(new Uint8Array(wrapped)) },
    },
    key: { __brand: 'VaultKey', key: dek },
  };
}

/**
 * Unlock a vault.
 *
 * Returns `null` for a wrong passphrase rather than throwing, because a wrong
 * passphrase is an ordinary event on a login screen and not an exception.
 * There is no separate verification step: AES-GCM authenticates, so the wrong
 * key simply fails to unwrap.
 */
export async function unlockVault(
  passphrase: string,
  record: VaultRecord,
): Promise<VaultKey | null> {
  try {
    const kek = await deriveKek(passphrase, fromBase64(record.salt), record.iterations);
    const dek = await subtle().unwrapKey(
      'raw',
      fromBase64(record.wrappedKey.data) as BufferSource,
      kek,
      { name: 'AES-GCM', iv: fromBase64(record.wrappedKey.iv) as BufferSource },
      { name: 'AES-GCM', length: DEK_BYTES * 8 },
      true,
      ['encrypt', 'decrypt'],
    );
    return { __brand: 'VaultKey', key: dek };
  } catch {
    return null;
  }
}

/**
 * Change the passphrase without touching the data.
 *
 * Unwraps the existing data key and rewraps it under a key derived from the
 * new passphrase. Every encrypted record stays exactly as it is.
 */
export async function changePassphrase(
  currentPassphrase: string,
  newPassphrase: string,
  record: VaultRecord,
  iterations: number = KDF_ITERATIONS,
): Promise<VaultRecord | null> {
  if (newPassphrase.length === 0) throw new Error('A vault passphrase cannot be empty');

  const key = await unlockVault(currentPassphrase, record);
  if (key === null) return null;

  const salt = randomBytes(SALT_BYTES);
  const kek = await deriveKek(newPassphrase, salt, iterations);
  const iv = randomBytes(IV_BYTES);
  const wrapped = await subtle().wrapKey('raw', key.key, kek, {
    name: 'AES-GCM',
    iv: iv as BufferSource,
  });

  return {
    version: 1,
    salt: toBase64(salt),
    iterations,
    wrappedKey: { iv: toBase64(iv), data: toBase64(new Uint8Array(wrapped)) },
  };
}

/**
 * Encrypt a value.
 *
 * A fresh initialisation vector is generated for every call. Reusing one under
 * the same key breaks AES-GCM completely, so it is generated here rather than
 * accepted as an argument, removing any way for a caller to get it wrong.
 */
export async function seal(key: VaultKey, value: unknown): Promise<SealedData> {
  const iv = randomBytes(IV_BYTES);
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await subtle().encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key.key,
    plaintext as BufferSource,
  );
  return { iv: toBase64(iv), data: toBase64(new Uint8Array(ciphertext)) };
}

/**
 * Decrypt a value.
 *
 * Returns `null` when the data was encrypted under a different key or has been
 * altered since. Both are indistinguishable here, and deliberately so: the
 * only safe response to either is to refuse the data.
 */
export async function unseal<T>(key: VaultKey, sealed: SealedData): Promise<T | null> {
  try {
    const plaintext = await subtle().decrypt(
      { name: 'AES-GCM', iv: fromBase64(sealed.iv) as BufferSource },
      key.key,
      fromBase64(sealed.data) as BufferSource,
    );
    return JSON.parse(new TextDecoder().decode(plaintext)) as T;
  } catch {
    return null;
  }
}
